import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  faArrowRight,
  faCircleNodes,
} from '@fortawesome/pro-regular-svg-icons';

import { AdminSection } from '@typings';
import { getClusters } from '@services';
import { useFetchList } from '@hooks';
import { path } from '@utils';

import { ChaseSpinner, Icon, Render, Table } from '@components';

const ITEMS_PER_PAGE = 50;

export const AdminSectionClustersPage = () => {
  const loaderRef = useRef<HTMLDivElement | null>(null);

  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  const { list, isFetched } = useFetchList({
    getList: getClusters,
  });

  const navigate = useNavigate();

  const handleClusterRowClick = (cluster: AdminSection.Cluster) => {
    if (cluster) {
      navigate(path.clusterDetails(cluster.name));
    }
  };

  const clustersToShow = useMemo(
    () => list.slice(0, visibleCount),
    [list, visibleCount],
  );

  useEffect(() => {
    if (!loaderRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + ITEMS_PER_PAGE, list.length),
          );
        }
      },
      {
        rootMargin: '100px',
      },
    );

    observer.observe(loaderRef.current);

    return () => observer.disconnect();
  }, [list.length]);

  if (!isFetched) {
    return (
      <div className="relative flex min-h-[400px] w-full items-center justify-center">
        <ChaseSpinner color="black" className="h-12 w-12" />
      </div>
    );
  }

  return (
    <>
      <Table className="mt-6 w-full auto-rows-min grid-cols-[min-content_repeat(2,minmax(max-content,auto))_min-content] overflow-auto">
        <Table.Header>
          <Table.Row>
            <Table.Head />
            <Table.Head>Name</Table.Head>
            <Table.Head>Location</Table.Head>
            <Table.Head />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {clustersToShow.map((cluster) => (
            <Table.Row
              key={cluster.name}
              className="hover-td group cursor-pointer"
              onClick={() => handleClusterRowClick(cluster)}
            >
              <Table.Cell>
                <div className="h-6 w-6 overflow-hidden rounded-full">
                  {cluster.logoUrl ? (
                    <img
                      src={cluster.logoUrl}
                      alt={cluster.name}
                      className="h-6 object-cover"
                    />
                  ) : (
                    <Icon
                      icon={faCircleNodes}
                      className="bg-secondary-dark text-white"
                    />
                  )}
                </div>
              </Table.Cell>
              <Table.Cell>{cluster.name}</Table.Cell>
              <Table.Cell>{cluster.location}</Table.Cell>
              <Table.Cell>
                <Icon
                  icon={faArrowRight}
                  className="h-6 w-6 rounded-full text-neural-04"
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      <div ref={loaderRef} className="h-10" />

      <Render if={list.length > visibleCount}>
        <div className="flex w-full items-center justify-center">
          <ChaseSpinner color="black" className="h-8 w-8" />
        </div>
      </Render>

      <Render if={list.length === 0}>
        <p className="break-words text-center text-neural-04">
          No clusters found
        </p>
      </Render>
    </>
  );
};
